import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import type { SkillExitSlipProgress } from '../../models/ExitSlip';

export interface ExitSlipSkillProgressState {
  // Exit-ticket progress per skill, keyed by skill id ("SkillNode:<n>"). A skill with no entry has
  // no exit tickets inferring it, and its unit-path card shows no badge at all.
  //
  // Redux rather than a prop for the same reason as exitSlipUnlockedSkillsSlice: DashboardCard sits
  // four levels inside the path useDashboardUnitPath builds, while the exit-ticket data is owned by
  // its sibling DashboardExitSlipQuery. Threading it down would mean adding a parameter to every
  // render helper in between, all of which have nothing to contribute to it.
  bySkillId: Record<string, SkillExitSlipProgress>;
}

const initialState: ExitSlipSkillProgressState = { bySkillId: {} };

export const exitSlipSkillProgressSlice = createSlice({
  name: 'exitSlipSkillProgress',
  initialState,
  reducers: {
    setExitSlipSkillProgress: (state: ExitSlipSkillProgressState, action: PayloadAction<SkillExitSlipProgress[]>) => {
      state.bySkillId = Object.fromEntries(action.payload.map(progress => [progress.skillId, progress]));
    },
  },
});

export const selectedExitSlipSkillProgress = (state: RootState) => state.exitSlipSkillProgress.bySkillId;

/**
 * One skill's exit-ticket progress, or undefined when no exit ticket infers it.
 *
 * Returns the stored object itself rather than anything derived, so a card only re-renders when its
 * own skill's entry is replaced — a selector building a new object here would return a fresh identity
 * on every store change and re-render every card on the path.
 */
export const selectExitSlipSkillProgressFor = (
  state: RootState,
  skillId: string | undefined,
): SkillExitSlipProgress | undefined => (skillId ? state.exitSlipSkillProgress.bySkillId[skillId] : undefined);

export const { setExitSlipSkillProgress } = exitSlipSkillProgressSlice.actions;

export default exitSlipSkillProgressSlice.reducer;
